import sanitizeHtml from 'sanitize-html';
import { marked, Renderer, type Tokens } from 'marked';
import React, { CSSProperties, useMemo } from 'react';

const ALLOWED_TAGS = [
  'a',
  'article',
  'b',
  'blockquote',
  'br',
  'caption',
  'code',
  'del',
  'details',
  'div',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'ins',
  'kbd',
  'li',
  'main',
  'ol',
  'p',
  'pre',
  'section',
  'span',
  'strong',
  'sub',
  'summary',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];
const GENERIC_ALLOWED_ATTRIBUTES = ['style', 'title'];

// Keep email formatting, but exclude executable content and CSS URL/expression sinks.
function sanitizer(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: ALLOWED_TAGS,
    allowedSchemes: ['http', 'https', 'mailto'],
    allowedAttributes: {
      '*': GENERIC_ALLOWED_ATTRIBUTES,
      img: ['src', 'alt', 'width', 'height'],
      table: ['width'],
      td: ['align', 'width'],
      th: ['align', 'width'],
      a: ['href', 'target', 'rel'],
      ol: ['start'],
      ul: ['start'],
    },
    allowedStyles: {
      '*': {
        color: [/^(?:#[\da-f]{3,8}|[a-z]+|rgba?\([\d.,%\s]+\))$/i],
        'background-color': [/^(?:#[\da-f]{3,8}|[a-z]+|rgba?\([\d.,%\s]+\))$/i],
        'font-family': [/^[\w\s,"'-]+$/],
        'font-size': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/],
        'font-weight': [/^(?:normal|bold|[1-9]00)$/],
        'font-style': [/^(?:normal|italic|oblique)$/],
        'text-align': [/^(?:left|center|right|justify)$/],
        'text-decoration': [/^(?:none|underline|line-through)$/],
        'line-height': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)?$/],
        'white-space': [/^(?:normal|pre|pre-wrap|pre-line|nowrap)$/],
        padding: [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)(?:\s+\d+(?:\.\d+)?(?:px|pt|em|rem|%)){0,3}$/],
      },
    },
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: attribs.target === '_blank' ? { ...attribs, rel: 'noopener noreferrer' } : attribs,
      }),
    },
  });
}

class CustomRenderer extends Renderer {
  override table(token: Tokens.Table) {
    return super.table(token).replace('<table>', '<table width="100%">');
  }

  override link(token: Tokens.Link) {
    // Let marked escape attribute values before sanitizing the complete HTML.
    return super.link(token).replace('<a ', '<a target="_blank" ');
  }
}

function renderMarkdownString(str: string): string {
  const html = marked.parse(str, {
    async: false,
    breaks: true,
    gfm: true,
    pedantic: false,
    silent: false,
    renderer: new CustomRenderer(),
  });
  if (typeof html !== 'string') {
    throw new Error('marked.parse did not return a string');
  }
  return sanitizer(html);
}

type Props = {
  style: CSSProperties;
  markdown: string;
};
export default function EmailMarkdown({ markdown, ...props }: Props) {
  const data = useMemo(() => renderMarkdownString(markdown), [markdown]);
  return <div {...props} dangerouslySetInnerHTML={{ __html: data }} />;
}
